import { useState, useMemo } from "react";
import { User, Mail, Lock, Eye, EyeOff, Shield, RefreshCw, Info } from "lucide-react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../context/ToastContext";
import { useCreateUser } from "../../api/users/useUsersApi";
import styles from "./AddUserModal.module.css";

const emptyForm = { name: "", email: "", password: "", roleId: "" };

const prettyRole = (name = "") =>
  name
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const generatePassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const nums = "23456789";
  const special = "!@#$%&*";
  const all = upper + lower + nums + special;
  let pwd =
    upper[Math.floor(Math.random() * upper.length)] +
    lower[Math.floor(Math.random() * lower.length)] +
    nums[Math.floor(Math.random() * nums.length)] +
    special[Math.floor(Math.random() * special.length)];
  for (let i = 0; i < 8; i++) pwd += all[Math.floor(Math.random() * all.length)];
  return pwd
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
};

const AddUserModal = ({ open, onClose, roles = [], onCreated }) => {
  const { showToast } = useToast();
  const { execute: createUser, loading } = useCreateUser();

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);

  const roleOptions = useMemo(
    () =>
      Array.isArray(roles)
        ? roles.filter((r) => r.status === "ACTIVE" && r.name !== "customer")
        : [],
    [roles]
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.password) e.password = "Password is required";
    else if (
      form.password.length < 8 ||
      !/[A-Z]/.test(form.password) ||
      !/[a-z]/.test(form.password) ||
      !/[0-9]/.test(form.password) ||
      !/[^A-Za-z0-9]/.test(form.password)
    )
      e.password =
        "Min 8 chars with uppercase, lowercase, number and special character";
    if (!form.roleId) e.roleId = "Select a role";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleClose = () => {
    setForm(emptyForm);
    setErrors({});
    setShowPwd(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        roles: Number(form.roleId),
      });
      showToast("User created successfully", "success");
      handleClose();
      onCreated?.();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to create user", "error");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Add New User"
      width="560px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={loading}>
            {loading ? "Creating…" : "Create User"}
          </button>
        </>
      }
    >
      <div className={styles.form}>
        {/* Name */}
        <div className={styles.field}>
          <label className={styles.label}>
            Full Name <span className={styles.req}>*</span>
          </label>
          <div className={styles.inputWrap}>
            <User size={16} className={styles.inputIcon} />
            <input
              className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          {errors.name && <span className={styles.errText}>{errors.name}</span>}
        </div>

        {/* Email */}
        <div className={styles.field}>
          <label className={styles.label}>
            Email Address <span className={styles.req}>*</span>
          </label>
          <div className={styles.inputWrap}>
            <Mail size={16} className={styles.inputIcon} />
            <input
              type="email"
              className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="jane.doe@sunsky.be"
            />
          </div>
          {errors.email && <span className={styles.errText}>{errors.email}</span>}
        </div>

        {/* Password */}
        <div className={styles.field}>
          <label className={styles.label}>
            Temporary Password <span className={styles.req}>*</span>
          </label>
          <div className={styles.inputWrap}>
            <Lock size={16} className={styles.inputIcon} />
            <input
              type={showPwd ? "text" : "password"}
              className={`${styles.input} ${styles.pwInput} ${
                errors.password ? styles.inputError : ""
              }`}
              value={form.password}
              onChange={(e) => setField("password", e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              className={styles.pwToggle}
              onClick={() => setShowPwd((s) => !s)}
              title={showPwd ? "Hide" : "Show"}
            >
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <div className={styles.pwRow}>
            {errors.password ? (
              <span className={styles.errText}>{errors.password}</span>
            ) : (
              <span className={styles.hint}>
                Min 8 chars · upper, lower, number &amp; special
              </span>
            )}
            <button
              type="button"
              className={styles.genBtn}
              onClick={() => {
                setField("password", generatePassword());
                setShowPwd(true);
              }}
            >
              <RefreshCw size={12} /> Generate
            </button>
          </div>
        </div>

        {/* Role */}
        <div className={styles.field}>
          <label className={styles.label}>
            Role <span className={styles.req}>*</span>
          </label>
          <div className={styles.roleGrid}>
            {roleOptions.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`${styles.roleCard} ${
                  String(form.roleId) === String(r.id) ? styles.roleCardActive : ""
                }`}
                onClick={() => setField("roleId", r.id)}
              >
                <Shield size={15} />
                <div className={styles.roleCardBody}>
                  <span className={styles.roleCardName}>{prettyRole(r.name)}</span>
                  {r.description && (
                    <span className={styles.roleCardDesc}>{r.description}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.roleId && <span className={styles.errText}>{errors.roleId}</span>}
        </div>

        <div className={styles.infoBox}>
          <Info size={15} />
          <span>
            The user inherits all permissions from the selected role. Fine-grained
            direct permissions can be added afterwards from the user's detail panel.
          </span>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddUserModal;
